import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axiosInstance from "@/lib/axios";

/**
 * Hook to proactively refresh the authentication token before it expires.
 * Default interval is 25 minutes (for a 30-minute token expiry).
 */
export function useAuthRefresh(intervalMs: number = 25 * 60 * 1000) {
  const { pathname } = useLocation();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Don't run on login page
    if (pathname === "/login") {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    // Function to perform the refresh
    const performRefresh = async () => {
      try {
        if (import.meta.env.DEV) {
          console.log("Proactive refresh: Starting...");
        }

        const success = await axiosInstance.refreshToken();

        if (import.meta.env.DEV) {
          if (success) {
            console.log("Proactive refresh: Success");
          } else {
            console.warn(
              "Proactive refresh: Failed (likely unauthenticated)",
            );
          }
        }
      } catch (error) {
        console.error("Proactive refresh: Error", error);
      }
    };

    // Start the interval if it doesn't exist
    if (!timerRef.current) {
      if (import.meta.env.DEV) {
        console.log(
          `Proactive refresh: Timer set for every ${intervalMs / 60000} minutes`,
        );
      }

      // Perform initial check/refresh after a small delay to avoid race conditions during boot
      const initialDelay = setTimeout(performRefresh, 5000);

      timerRef.current = setInterval(performRefresh, intervalMs);

      return () => {
        clearTimeout(initialDelay);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      };
    }
  }, [pathname, intervalMs]);

  return null;
}
